import classNames from 'classnames'
import { add, format, isPast, sub } from 'date-fns'
import { uk } from 'date-fns/locale'
import React, { type Dispatch, type SetStateAction, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { ChildrenSelectList } from '@components/ChildrenSelectList'
import { ClassroomSelectList } from '@components/ClassroomSelectList'

import { Button } from '@UI/Button'
import { Notifications } from '@UI/Notifications'
import { Tabs } from '@UI/Tabs'

import { useActions } from '@hooks/useActions'
import { useHeader } from '@hooks/useHeader'
import useOnClickOutside from '@hooks/useOnClickOutside'
import { useQueryParams } from '@hooks/useQueryParams'

import { useGetHasUnreadQuery } from '@store/notifications/notifications.api'
import { useGetInfoAbutMeQuery } from '@store/profile/profile.api'
import { useVisitLessonByStudentMutation } from '@store/student/student.api'

import type { MenuProps, TabsProps } from './Header.props'
import { HeaderClock } from './components/HeaderClock'
import { ScheduleStepper } from './components/ScheduleStepper'
import { ScheduleTypeSwitcher } from './components/ScheduleTypeSwitcher'
import { UserBlock } from './components/UserBlock'

export function Header() {
  const header = useHeader()

  const { data: aboutMe } = useGetInfoAbutMeQuery({})

  const { data: hasNotifications } = useGetHasUnreadQuery({})

  const notificationsRef = useRef<HTMLDivElement>(null)

  const [showNotifications, setShowNotifications] = useState(false)

  const handleHideNotifications = () => setShowNotifications(false)
  const handleToggleShowNotifications = () => setShowNotifications((prev) => !prev)

  useOnClickOutside(notificationsRef, handleHideNotifications)

  const Content = () => {
    switch (header?.type) {
      case 'text':
      case 'classroom':
        return (
          <>
            {header.props.iconSpriteId && (
              <svg className={header.props.iconClassName}>
                <use href={`/img/sprite.svg#${header.props.iconSpriteId}`}></use>
              </svg>
            )}
            <h2 className="header__title">{header.props.title}</h2>
            {header.props?.button && (
              <Button
                className="header__link"
                {...header.props.button}
              >
                {header.props.button.children}
              </Button>
            )}
          </>
        )
      case 'day-tabs':
        return <DayTabs {...header.props} />
      case 'menu':
        return <Menu {...header.props} />
      case 'schedule':
        return <ScheduleStepper />
      case 'custom':
        return (
          <>
            {header.props?.title && <h2 className="header__title">{header.props.title}</h2>}
            {header.props?.left}
          </>
        )
      default:
        return null
    }
  }

  const ContentRight = () => {
    switch (header?.type) {
      case 'schedule':
        return <ScheduleTypeSwitcher />
      case 'classroom':
        return <ClassroomSelectList />
      case 'custom':
        return <>{header.props?.right}</>
      default:
        return null
    }
  }

  return (
    <header className={classNames('header', header?.className)}>
      <div className="header__container container">
        <div className={classNames('header__inner', { 'header--wrap': header?.type === 'custom' })}>
          <div className="header__left">
            <Content />
          </div>

          {header?.type === 'custom' && header.props?.center}

          <div className="header__right">
            <ContentRight />
            {aboutMe?.role === 10 && <ChildrenSelectList />}

            <HeaderClock />

            <div className="header__user">
              <div
                ref={notificationsRef}
                className="header__block"
              >
                <button
                  className={classNames('header__notifications', { 'header__notifications--active': hasNotifications })}
                  onClick={handleToggleShowNotifications}
                >
                  <svg className="header__notifications-svg">
                    <use href="/img/sprite.svg#notifications"></use>
                  </svg>
                </button>

                {showNotifications && (
                  <div className={classNames('header__wrapper', 'header__wrapper--active')}>
                    <Notifications
                      className={'header__ntf'}
                      onClose={handleHideNotifications}
                      hasUnread={hasNotifications}
                    />
                  </div>
                )}
              </div>

              <UserBlock />
            </div>
          </div>
        </div>
      </div>
    </header>
  )
}

function DayTabs({ title }: TabsProps) {
  const { getQuery, setQuery, removeQuery } = useQueryParams()

  const [activeTabs, setActiveTabs] = useState<number>(!!getQuery('day') ? 2 : 1)

  const handleSetDay = (activeTab: number) => {
    if (activeTab === 1) {
      removeQuery('day')
      setActiveTabs(activeTab)
    } else {
      setQuery('day', 'tomorrow')
      setActiveTabs(activeTab)
    }
  }

  return (
    <>
      <h2 className="header__title">{title}</h2>

      <Tabs
        className="header__content-tabs"
        list={['на сьогодні', 'на завтра']}
        activeTab={activeTabs}
        setActiveTab={handleSetDay as Dispatch<SetStateAction<number>>}
        isSmall
      />
    </>
  )
}

function Menu({ title, link, date, isVisited }: MenuProps) {
  const navigate = useNavigate()
  const { getQuery } = useQueryParams()

  const { setLessonsNavShow } = useActions()

  const { data: aboutMe } = useGetInfoAbutMeQuery({})
  const [visitLesson] = useVisitLessonByStudentMutation()

  const isFinished = isPast(add(new Date(date!), { minutes: 45 }))
  const isAvailable = isPast(sub(new Date(date!), { minutes: 5 }))

  const handleOnClick = () => {
    const lessonId = getQuery('lessonId')

    if (aboutMe && aboutMe?.role === 2 && lessonId) {
      visitLesson(lessonId)
    }
    window.open(link, '_blank')?.focus()
  }

  return (
    <>
      <div className="header__lesson">
        <span
          className="header__back"
          onClick={() => navigate(-1)}
        >
          <svg className="header__back-svg">
            <use href="/img/sprite.svg#arrow-right"></use>
          </svg>
        </span>
        <button
          className="header__manual"
          onClick={() => setLessonsNavShow(true)}
        >
          <svg className="header__manual-svg">
            <use href="/img/sprite.svg#list"></use>
          </svg>
        </button>
        <h2 className="header__title">{title}</h2>
      </div>
      {!!date && (
        <div className="header__go">
          {isFinished ? (
            <Button
              variant={'gray'}
              className="header__link"
            >
              <svg className="btn__icon">
                <use href={`/img/sprite.svg#${isVisited ? 'check' : 'close'}`}></use>
              </svg>
              Урок {isVisited ? 'пройшов' : 'пропущено'}
            </Button>
          ) : (
            <Button
              className="header__link"
              onClick={handleOnClick}
              disabled={!isAvailable}
            >
              <svg className="btn__icon">
                <use href="/img/sprite.svg#webcam"></use>
              </svg>
              {isAvailable ? 'Перейти до уроку' : 'Урок очікується'}
            </Button>
          )}

          <time
            className="header__date"
            dateTime={date}
          >
            {format(new Date(date), "d MMMM', 'H:mm", { locale: uk })}
          </time>
        </div>
      )}
    </>
  )
}
